import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { PageHero } from "@/components/shared/PageHero";
import { CTASection } from "@/components/shared/CTASection";
import { CheckCircle, ArrowRight, Loader2 } from "lucide-react";
import { projects as staticProjects } from "@/data/projects";

// Default image for projects
import defaultProjectImage from "@/assets/hero-lawn-striped.jpg";

const Projects = () => {
  const { data: dbProjects, isLoading } = useQuery({
    queryKey: ['projects'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('projects')
        .select('*')
        .eq('published', true)
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      return data;
    },
  });

  // Combine database projects with static projects
  const allProjects = [
    ...(dbProjects || []),
    ...staticProjects,
  ];

  return (
    <>
      <Helmet>
        <title>Our Projects | Riekels Lawn Care & Landscaping</title>
        <meta
          name="description"
          content="View our recent lawn care and landscaping projects in the West Twin Cities Metro and surrounding areas. See examples of our quality work."
        />
      </Helmet>
      <main>
        <PageHero
          badge="Our Work"
          title="Recent Projects"
          subtitle="See examples of the quality lawn care and landscaping we deliver to our neighbors."
        />

        <section className="py-20">
          <div className="container mx-auto px-4">
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-secondary" />
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {allProjects.map((project) => (
                  <Link
                    key={project.slug}
                    to={`/projects/${project.slug}`}
                    className="feature-card group hover:border-secondary/50 transition-colors"
                  >
                    {(project.image_url || true) && (
                      <div className="aspect-video overflow-hidden rounded-lg mb-4 -mx-6 -mt-6">
                        <img
                          src={project.image_url || defaultProjectImage}
                          alt={project.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                      </div>
                    )}
                    <div className="mb-4">
                      <span className="text-sm font-medium text-secondary">{project.location}</span>
                    </div>
                    <h3 className="text-xl font-heading font-bold text-foreground mb-3 group-hover:text-secondary transition-colors">
                      {project.title}
                    </h3>
                    <p className="text-muted-foreground mb-4">
                      {project.description}
                    </p>
                    <ul className="space-y-2 mb-4">
                      {project.highlights.map((highlight, i) => (
                        <li key={i} className="flex items-center gap-2 text-sm text-muted-foreground">
                          <CheckCircle className="w-4 h-4 text-secondary flex-shrink-0" />
                          {highlight}
                        </li>
                      ))}
                    </ul>
                    <div className="flex items-center text-secondary font-semibold text-sm">
                      View Project <ArrowRight className="w-4 h-4 ml-1" />
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </section>

        <CTASection />
      </main>
    </>
  );
};

export default Projects;
